import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import * as fixedAssetService from '../services/fixedAssetService';

const FixedAssetCategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    depreciationMethod: 'Straight Line',
    defaultDepreciationRate: '',
    usefulLifeYears: '',
    isActive: true
  });

  useEffect(() => {
    if (isEdit) {
      fetchCategory();
    }
  }, [id]);

  const fetchCategory = async () => {
    try {
      const res = await fixedAssetService.getCategoryById(id);
      if (res.success) {
        setFormData({
          name: res.data.name || '',
          description: res.data.description || '',
          depreciationMethod: res.data.depreciationMethod || 'Straight Line',
          defaultDepreciationRate: res.data.defaultDepreciationRate || '',
          usefulLifeYears: res.data.usefulLifeYears || '',
          isActive: res.data.isActive
        });
      }
    } catch (error) {
      console.error('Failed to fetch category', error);
      alert('Failed to load category details');
      navigate('/fixed-assets/categories');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (isEdit) {
        await fixedAssetService.updateCategory(id, formData);
      } else {
        await fixedAssetService.createCategory(formData);
      }
      navigate('/fixed-assets/categories');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save category');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/fixed-assets/categories')}>
          Back
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {isEdit ? 'Edit Asset Category' : 'New Asset Category'}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid  size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Category Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Depreciation Method"
                name="depreciationMethod"
                value={formData.depreciationMethod}
                onChange={handleChange}
              >
                <MenuItem value="Straight Line">Straight Line</MenuItem>
                <MenuItem value="Declining Balance">Declining Balance</MenuItem>
                <MenuItem value="None">None</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Default Depreciation Rate (%)"
                name="defaultDepreciationRate"
                type="number"
                inputProps={{ step: "0.01", min: "0", max: "100" }}
                value={formData.defaultDepreciationRate}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Useful Life (Years)"
                name="usefulLifeYears"
                type="number"
                inputProps={{ min: "1" }}
                value={formData.usefulLifeYears}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleChange}
                    name="isActive"
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={() => navigate('/fixed-assets/categories')} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                  sx={{ bgcolor: '#4ECDC4', '&:hover': { bgcolor: '#3BA89F' } }}
                >
                  {saving ? 'Saving...' : 'Save Category'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default FixedAssetCategoryForm;
